import { Loader } from '@components'
import BackButton from '@components/BackButton'
import { CustomInput } from '@components/CustomInput'
import {
  extractCheckoutMethodFieldOptionLabel,
  isCheckoutMethodFieldReadOnly,
  prepareCheckoutMethodFieldOptions
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, UseAdditionalFieldsOutput } from 'mmfintech-checkout-commons/types'
import {
  formatCardNumber,
  formatCardValidityPartial,
  getCountryAlpha2,
  isValidArray,
  parseCardValidity,
  sleep,
  tr
} from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { Fragment } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import styles from './Blixcard.module.scss'
import { VoucherTitle } from './Blixcard.styled'

type BlixcardFieldsStepProps = {
  countryCode: string
  paymentMethod: string
  fieldValues: UseAdditionalFieldsOutput
  handleBack: () => void
  handleNext: () => void
}

export const BlixcardFieldsStep = (props: BlixcardFieldsStepProps) => {
  const { countryCode, paymentMethod, fieldValues, handleBack, handleNext } = props

  const { checkoutPayError, checkoutPayFetching } = useSelector(
    ({ checkout: { checkoutPayError, checkoutPayFetching } }) => ({
      checkoutPayError,
      checkoutPayFetching
    }),
    shallowEqual
  )

  const handleSubmit = () => {
    if (fieldValues.hasBankSelection) {
      fieldValues.setRequired('bankChoiceId', false)
    }

    sleep(200).then(() => {
      if (fieldValues.areValid()) {
        handleNext()
      }
    })
  }

  return (
    <>
      {typeof handleBack === 'function' ? (
        <div className='mb-2'>
          <BackButton onClick={handleBack} />
        </div>
      ) : null}

      <VoucherTitle data-test={`pay-with-${paymentMethod}-banks`}>
        {tr('CHECKOUT.VOUCHER.FIELDS.TITLE', 'Pay with {{METHOD}}', {
          METHOD: tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)
        })}
      </VoucherTitle>

      <ErrorDisplay error={checkoutPayError} />

      <div>
        {isValidArray(fieldValues.fields)
          ? fieldValues.fields.map((field: CheckoutMethodField) => {
              if (
                fieldValues.isFieldHidden(field) ||
                field.name === 'bankChoiceId' ||
                field.name === 'expiryYear' ||
                field.name === 'cvv'
              ) {
                return null
              }

              if (field.name === 'cardNumber') {
                return (
                  <Fragment key={field.name}>
                    <CustomInput
                      type='text'
                      name={field.name}
                      data-test={`${field.name}-input`}
                      label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                      placeholder='0000 0000 0000 0000'
                      value={formatCardNumber(fieldValues.getValue('cardNumber'))}
                      onChange={(_: string, value: string) =>
                        fieldValues.setValue('cardNumber', value.replace(/\D+/g, ''))
                      }
                      error={fieldValues.getError(field.name)}
                      maxLength={19}
                      autoComplete='off'
                      hideErrorLine
                    />
                    {Boolean(field.description) && (
                      <div className='input-description'>
                        {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                      </div>
                    )}
                  </Fragment>
                )
              }

              if (field.name === 'expiryMonth') {
                return (
                  <div className={styles['double-container']} key={field.name}>
                    <CustomInput
                      type='text'
                      name='expiryDate'
                      data-test='expiryDate-input'
                      label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.EXPIRY_DATE', 'Expiry date')}
                      placeholder={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.EXPIRY_DATE_PLACEHOLDER', 'MM/YY')}
                      value={formatCardValidityPartial([
                        fieldValues.getValue('expiryMonth'),
                        fieldValues.getValue('expiryYear')
                      ])}
                      onChange={(_: string, value: string) => {
                        const [month, year] = parseCardValidity(value)
                        fieldValues.setValue('expiryMonth', month)
                        fieldValues.setValue('expiryYear', year)
                      }}
                      error={fieldValues.getError('expiryMonth') || fieldValues.getError('expiryYear')}
                      maxLength={5}
                      required
                      hideErrorLine
                      hideShowPassword
                      autoComplete='off'
                    />
                    <CustomInput
                      type='password'
                      data-test='cvv-input'
                      label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.CVV', 'CVV')}
                      {...fieldValues.registerInput('cvv')}
                      maxLength={4}
                      hideErrorLine
                      hideShowPassword
                      autoComplete='off'
                    />
                  </div>
                )
              }

              if (field.type.toLowerCase() === 'select') {
                return (
                  <Fragment key={field.name}>
                    {isCheckoutMethodFieldReadOnly(field) ? (
                      <CustomInput
                        type='text'
                        data-test={`${field.name}-input`}
                        label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                        value={extractCheckoutMethodFieldOptionLabel(field, fieldValues.getValue(field.name))}
                        readOnly={true}
                        hideErrorLine
                      />
                    ) : (
                      <CustomInput
                        type='select'
                        data-test={`${field.name}-select`}
                        options={prepareCheckoutMethodFieldOptions(field)}
                        label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                        {...fieldValues.registerInput(field.name)}
                        menuPlacement='top'
                        hideErrorLine
                      />
                    )}
                    {Boolean(field.description) && (
                      <div className='input-description'>
                        {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                      </div>
                    )}
                  </Fragment>
                )
              }

              if (field.name === 'phone') {
                return (
                  <Fragment key={field.name}>
                    <CustomInput
                      type='phone'
                      data-test={`${field.name}-input`}
                      label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                      {...fieldValues.registerInput(field.name)}
                      disabled={isCheckoutMethodFieldReadOnly(field)}
                      autoComplete='off'
                      hideErrorLine
                      {...(paymentMethod === 'INTERAC'
                        ? {
                            country: 'ca',
                            onlyCountries: ['ca'],
                            countryCodeEditable: false,
                            disableDropdown: true
                          }
                        : {
                            country: getCountryAlpha2(countryCode)?.toLowerCase()
                          })}
                    />
                    {Boolean(field.description) && (
                      <div className='input-description'>
                        {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                      </div>
                    )}
                  </Fragment>
                )
              }

              if (field.type.toLowerCase() === 'date') {
                return (
                  <Fragment key={field.name}>
                    <CustomInput
                      type='date'
                      data-test={`${field.name}-input`}
                      label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                      {...fieldValues.registerInput(field.name)}
                      readOnly={isCheckoutMethodFieldReadOnly(field)}
                      autoComplete='off'
                      hideErrorLine
                    />
                    {Boolean(field.description) && (
                      <div className='input-description'>
                        {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                      </div>
                    )}
                  </Fragment>
                )
              }

              return (
                <Fragment key={field.name}>
                  <CustomInput
                    type={field.sensitive ? 'password' : 'text'}
                    data-test={`${field.name}-input`}
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    {...fieldValues.registerInput(field.name)}
                    readOnly={isCheckoutMethodFieldReadOnly(field)}
                    autoComplete='off'
                    hideErrorLine
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            })
          : null}
      </div>

      <div className='buttons-container mt-2'>
        <button type='button' disabled={checkoutPayFetching} className='continue-btn' onClick={handleSubmit}>
          {checkoutPayFetching ? <Loader /> : tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
        </button>
      </div>
    </>
  )
}
